import React, { useEffect, useMemo, useState } from "react";

// Firebase
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { COLLECTION_NAME } from "../../constants/endpoints";

// PDF Libraries
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Icons
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FiEye, FiDownload } from "react-icons/fi";
import { FaLock, FaUnlock, FaUserSlash, FaUserCheck } from "react-icons/fa";

// Custom Components
import DeleteConfirmationModal from "../../Components/DeleteConfirmationModal/DeleteConfirmationModal";
import UserDetailModal from "../../Components/UserDetailModal/UserDetailModal";
import DeactivateAccountModal from "../../Components/DeactivateAccountModal/DeactivateAccountModal";
import Table from "../../Components/Table/Table";
import { formatDate, formatDateTime } from "../../utils";
import Loader from "../../Components/Loader/Loader";

const AllDriver = () => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rideToDelete, setRideToDelete] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [updatingLockedUserId, setUpdatingLockedUserId] = useState(null);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "deactivated"

  const formatCurrencyOrNA = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? `$${parsed.toFixed(2)}` : "N/A";
  };

  const formatWalletResetAt = (value) => {
    if (!value) return "N/A";

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? "N/A" : formatDateTime(value);
    }

    if (typeof value === "object" && typeof value.seconds === "number") {
      return formatDateTime(new Date(value.seconds * 1000));
    }

    if (typeof value === "number") {
      const millis = value > 1e12 ? value : value * 1000;
      const date = new Date(millis);
      return Number.isNaN(date.getTime()) ? "N/A" : formatDateTime(date);
    }

    if (typeof value === "string") {
      const parsed = Date.parse(value);
      if (Number.isNaN(parsed)) return "N/A";
      return formatDateTime(new Date(parsed));
    }

    return "N/A";
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(
        collection(db, COLLECTION_NAME.users)
      );
      let dataList = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt
            ? new Date(
              data.createdAt.seconds * 1000 +
              data.createdAt.nanoseconds / 1000000
            )
            : null,
          lastLogin: data.lastLogin
            ? new Date(
              data.lastLogin.seconds * 1000 +
              data.lastLogin.nanoseconds / 1000000
            )
            : null,
          lockedUntil: data.lockedUntil
            ? new Date(
              data.lockedUntil.seconds * 1000 +
              data.lockedUntil.nanoseconds / 1000000
            )
            : null,
          accountStatus: data.accountStatus || "active", // Default to active if not set
        };
      });

      // Filter by role and document status
      dataList = dataList.filter(
        (user) =>
          ["Rider", "Driver"].includes(user.role) &&
          user.documentStatus === "approved"
      );

      // Filter by account status
      if (statusFilter !== "all") {
        dataList = dataList.filter(
          (user) => user.accountStatus === statusFilter
        );
      }

      // Sort by created date
      dataList = dataList.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return sortOrder === "desc"
          ? b.createdAt - a.createdAt
          : a.createdAt - b.createdAt;
      });

      setData(dataList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data: ", error);
      setLoading(false);
    }
  };

  const fetchBankDetails = async (userId) => {
    try {
      const bankDocRef = doc(db, "BankDetails", userId);
      const bankDoc = await getDoc(bankDocRef);
      if (bankDoc.exists()) {
        return bankDoc.data();
      }
      return null;
    } catch (err) {
      console.error("Error fetching bank details:", err);
      return null;
    }
  };

  const openModal = async (user) => {
    let userWithBankDetails = { ...user };

    if (user.role === "Rider") {
      const bankDetails = await fetchBankDetails(user.id);
      userWithBankDetails = { ...userWithBankDetails, bankDetails };
    }

    setSelectedUser(userWithBankDetails);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedUser(null);
    setIsModalOpen(false);
  };

  const openDeleteModal = (ride) => {
    setRideToDelete(ride);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setRideToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleDelete = async () => {
    if (rideToDelete) {
      try {
        await deleteDoc(doc(db, COLLECTION_NAME.users, rideToDelete.id));
        setData((prevData) =>
          prevData.filter((ride) => ride.id !== rideToDelete.id)
        );
      } catch (error) {
        console.error("Error deleting document: ", error);
      }
      closeDeleteModal();
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "desc" ? "asc" : "desc"));
  };

  const isUserLocked = (user) => {
    return user.lockedUntil && user.lockedUntil > new Date();
  };

  const unlockUser = async (userId) => {
    setUpdatingLockedUserId(userId);
    try {
      const userRef = doc(db, COLLECTION_NAME.users, userId);
      await updateDoc(userRef, { lockedUntil: null });

      setData((prevData) =>
        prevData.map((user) =>
          user.id === userId ? { ...user, lockedUntil: null } : user
        )
      );
    } catch (error) {
      console.error("Error unlocking user:", error);
      alert("Failed to unlock user. Please try again.");
    } finally {
      setUpdatingLockedUserId(null);
    }
  };

  const openDeactivateModal = (user) => {
    setUserToDeactivate(user);
    setIsDeactivateModalOpen(true);
  };

  const closeDeactivateModal = () => {
    setUserToDeactivate(null);
    setIsDeactivateModalOpen(false);
  };

  const handleDeactivateSuccess = () => {
    fetchData(); // Refresh the data after deactivation/reactivation
  };

  const exportToPDF = () => {
    if (!data || data.length === 0) {
      alert("No data to export");
      return;
    }

    const doc = new jsPDF();

    // Add title
    doc.setFontSize(24);
    doc.setTextColor(59, 130, 246); // Blue color
    doc.text("Driver List", 105, 25, { align: "center" });

    // Add subtitle with date
    doc.setFontSize(12);
    doc.setTextColor(107, 114, 128); // Gray color
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, 35, {
      align: "center",
    });

    // Add line separator
    doc.setDrawColor(229, 231, 235);
    doc.line(20, 45, 190, 45);

    // Prepare table data
    const tableData = data.map((driver, index) => [
      index + 1,
      driver.fullName || "N/A",
      driver.email || "N/A",
      driver.phoneNumber || "N/A",
    ]);

    // Add table using autoTable plugin
    autoTable(doc, {
      startY: 55,
      head: [["#", "Name", "Email Address", "Phone Number"]],
      body: tableData,
      theme: "grid",
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontSize: 10,
        fontStyle: "bold",
      },
      bodyStyles: {
        fontSize: 8,
        textColor: 55,
        lineColor: [229, 231, 235],
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      margin: { top: 20, right: 20, bottom: 20, left: 20 },
      styles: {
        cellPadding: 2,
        lineWidth: 0.5,
      },
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Page ${i} of ${pageCount}`,
        105,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }

    // Save the PDF
    const now = new Date();

    const dateStr = now
      .toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      })
      .replace(/\//g, "-");

    const fileName = `Driver List ${dateStr}.pdf`;
    doc.save(fileName);
  };

  const columns = useMemo(
    () => [
      { Header: "Name", accessor: "fullName" },
      { Header: "Email Address", accessor: "email" },
      { Header: "Phone", accessor: "phoneNumber" },
      {
        Header: "Previous Week Income",
        accessor: "previousWeekIncome",
        Cell: ({ value }) => formatCurrencyOrNA(value),
      },
      {
        Header: "Wallet Reset At",
        accessor: "walletResetAt",
        Cell: ({ value }) => formatWalletResetAt(value),
      },
      {
        Header: "Lock Status",
        accessor: "lockStatus",
        Cell: ({ row }) => {
          const user = row.original;
          const locked = isUserLocked(user);

          return (
            <div
              className={`flex items-center gap-2 ${locked ? "text-red-600" : "text-green-600"
                }`}
            >
              {locked ? (
                <>
                  <FaLock />
                  <span>Locked until {formatDateTime(user.lockedUntil)}</span>
                </>
              ) : (
                <>
                  <FaUnlock />
                  <span>Unlocked</span>
                </>
              )}
            </div>
          );
        },
      },
      {
        Header: "Account Status",
        accessor: "accountStatus",
        Cell: ({ value }) => (
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${value === "deactivated"
              ? "bg-red-100 text-red-800"
              : "bg-green-100 text-green-800"
              }`}
          >
            {value === "deactivated" ? "Deactivated" : "Active"}
          </span>
        ),
      },
      // {
      //   Header: "Locked Actions",
      //   accessor: "lockedUntil",
      //   Cell: ({ row }) => {
      //     const user = row.original;
      //     const isLocked = isUserLocked(user);

      //     return isLocked ? (
      //       <button
      //         onClick={() => unlockUser(user.id)}
      //         disabled={updatingLockedUserId === user.id}
      //         className="text-sm text-red-600 hover:underline disabled:opacity-50"
      //       >
      //         {updatingLockedUserId === user.id ? "Unlocking..." : "Unlock Now"}
      //       </button>
      //     ) : null;
      //   },
      // },

      // {
      //   Header: "Last Login",
      //   accessor: "lastLogin",
      //   Cell: ({ value }) => formatDateTime(value),
      // },
      // {
      //   Header: (
      //     <div
      //       onClick={toggleSortOrder}
      //       className="cursor-pointer flex gap-1 items-center"
      //     >
      //       Created At{" "}
      //       {sortOrder === "desc" ? <IoIosArrowDown /> : <IoIosArrowUp />}
      //     </div>
      //   ),
      //   accessor: "createdAt",
      //   Cell: ({ value }) => formatDate(value),
      // },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="flex gap-2 items-center">
            <button
              className="text-primary hover:text-dazzledBlue text-lg"
              onClick={() => openModal(row.original)}
              title="View Details"
            >
              <FiEye />
            </button>
            <button
              className={`text-lg ${row.original.accountStatus === "deactivated"
                ? "text-green-600 hover:text-green-700"
                : "text-red-600 hover:text-red-700"
                }`}
              onClick={() => openDeactivateModal(row.original)}
              title={
                row.original.accountStatus === "deactivated"
                  ? "Reactivate Account"
                  : "Deactivate Account"
              }
            >
              {row.original.accountStatus === "deactivated" ? (
                <FaUserCheck />
              ) : (
                <FaUserSlash />
              )}
            </button>
          </div>
        ),
      },
    ],
    [sortOrder, updatingLockedUserId]
  );

  useEffect(() => {
    fetchData();
  }, [sortOrder, statusFilter]);

  return (
    <div className="px-1 sm:px-1 md-px-4 min-h-screen">
      <div className="flex items-center justify-between mt-8 md:mt-5 mb-2 md:mb-5">
        <h1 className="text-2xl md:text-3xl font-bold text-primary">
          Driver List
        </h1>
        <button
          onClick={exportToPDF}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
        >
          <FiDownload className="text-lg" />
          Export PDF
        </button>
      </div>

      {/* Status Filter */}
      <div className="flex mb-4 items-center gap-2">
        <span className="text-gray-700 font-medium">Filter by Status:</span>
        <div className="flex space-x-1">
          <button
            className={`px-4 py-2 rounded-md transition-colors ${statusFilter === "all"
              ? "bg-celadon text-primary"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            onClick={() => setStatusFilter("all")}
          >
            All
          </button>
          <button
            className={`px-4 py-2 rounded-md transition-colors ${statusFilter === "active"
              ? "bg-celadon text-primary"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            onClick={() => setStatusFilter("active")}
          >
            Active
          </button>
          <button
            className={`px-4 py-2 rounded-md transition-colors ${statusFilter === "deactivated"
              ? "bg-celadon text-primary"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            onClick={() => setStatusFilter("deactivated")}
          >
            Deactivated
          </button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        data && (
          <div>
            <Table columns={columns} data={data} />

            <UserDetailModal
              isOpen={isModalOpen}
              onRequestClose={closeModal}
              user={selectedUser}
            />

            <DeleteConfirmationModal
              isOpen={isDeleteModalOpen}
              onRequestClose={closeDeleteModal}
              onConfirm={handleDelete}
            />

            <DeactivateAccountModal
              isOpen={isDeactivateModalOpen}
              onRequestClose={closeDeactivateModal}
              user={userToDeactivate}
              onSuccess={handleDeactivateSuccess}
            />
          </div>
        )
      )}
    </div>
  );
};

export default AllDriver;

